import {
  useTranslate,
  IResourceComponentsProps,
  CrudFilters,
  HttpError,
  useApiUrl,
  useNotification,
} from "@pankod/refine-core";
import {
  List,
  Table,
  TextField,
  useTable,
  getDefaultSortOrder,
  Space,
  EditButton,
  Form,
  Input,
  Icons,
  Button,
  ListButton,
  DeleteButton,
} from "@pankod/refine-antd";
import { useNavigation } from '../../core/customNavigation';
import { IPriceZone } from "interfaces";
import { PlusSquareOutlined, SyncOutlined } from "@ant-design/icons";
import { useState } from "react";
import axios from "axios";
import dayjs from 'dayjs'
import localizedFormat from 'dayjs/plugin/localizedFormat'
import 'dayjs/locale/fr'

export const PriceZoneList: React.FC<IResourceComponentsProps> = () => {
  const t = useTranslate();
  const { create } = useNavigation()

  const { tableProps, sorter, searchFormProps } = useTable<IPriceZone, HttpError, IPriceZoneFilterVariables>({
      onSearch: (params) => {
      const filters: CrudFilters = [];
      const { name } = params;
      filters.push(
          {
              field: "name",
              operator: "contains",
              value: name,
          },
      );
      return filters;
    },
    initialSorter: [
      {
        field: "name",
        order: "asc",
      },
    ],
  });

    const isLoading = false;
    const apiUrl = useApiUrl();
    const { open } = useNotification();
    const [syncingId, setSyncingId] = useState<string | "all" | null>(null);

    const authHeaders = () => ({
      headers: {
        Authorization: `Bearer ${localStorage.getItem("ACCESS_TOKEN")}`,
        "Content-Type": "application/json",
      },
      timeout: 600000, // 10 min (resync_all peut être long)
    });

    // Resync d'UNE pricezone → push bulk hier→+6 mois
    const resyncOne = async (id: string, name: string) => {
      setSyncingId(id);
      try {
        await axios.post(`${apiUrl}/pricezones/${id}/resync/`, {}, authHeaders());
        open?.({ type: "success", message: `Resync OK : ${name}` });
      } catch (e: any) {
        open?.({ type: "error", message: `Resync échoué : ${name}`, description: e?.message });
      } finally {
        setSyncingId(null);
      }
    };

    // Resync de TOUTES les pricezones
    const resyncAll = async () => {
      setSyncingId("all");
      try {
        const { data } = await axios.post(`${apiUrl}/pricezones/resync_all/`, {}, authHeaders());
        if (data?.success) {
          open?.({ type: "success", message: `Resync all OK : ${data.synced}/${data.total} zones` });
        } else {
          const ko = (data?.results || []).filter((r: any) => !r.success).map((r: any) => r.name).join(", ");
          open?.({ type: "error", message: `Resync all partiel : ${data?.synced}/${data?.total}`, description: `Échecs : ${ko}` });
        }
      } catch (e: any) {
        open?.({ type: "error", message: "Resync all échoué", description: e?.message });
      } finally {
        setSyncingId(null);
      }
    };

  return (
    <List 
      title={t("menu.pricezones")}
      pageHeaderProps={{
        extra: [
          <Form 
            layout="horizontal"
            onValuesChange={() => searchFormProps.form?.submit()}
            key="page_header_extra_1"
            {...searchFormProps}
          >
            <Form.Item label="" name="name" style={{ marginBottom: 0 }}>
              <Input
                  placeholder="ID, Title, Content, etc."
                  prefix={<Icons.SearchOutlined />}
              />
            </Form.Item>
          </Form>,
          <Button key="page_header_extra_2" onClick={(e) => create(t("menu.pricezones", e))}>+</Button>,
          <Button
            key="page_header_extra_resync"
            type="primary"
            icon={<SyncOutlined spin={syncingId === "all"} />}
            loading={syncingId === "all"}
            disabled={syncingId !== null}
            onClick={resyncAll}
          >
            Resync all
          </Button>,

        ],
      }}
    >
      <Table
        {...tableProps} 
        pagination={{
          ...tableProps.pagination,
          showSizeChanger: true,
        }}
        rowKey="id"
      >
        <Table.Column<IPriceZone>
          dataIndex="name"
          key="name"
          title={t("rooms.room")}
          render={(_, record) => {
            return(
              <Space>
                <TextField value={record?.name} />
                <EditButton hideText size="small" resourceNameOrRouteName="pricezones" recordItemId={record?.id}></EditButton>
                <DeleteButton hideText size="small" recordItemId={record.id} />
                </Space>
            )}}
  
          defaultSortOrder={getDefaultSortOrder("name", sorter)}
          sorter
        />
        <Table.Column<IPriceZone>

          title={t("table.actions")}
          dataIndex="actions"
          render={(_, record) => {
          return(
            <Space>
              {record?.yieldanalysis?.[0]?.id && <EditButton size="small" resourceNameOrRouteName="yieldanalysis" recordItemId={record?.yieldanalysis?.[0]?.id}>{`${t("buttons.edit")} ${t("menu.yield")} ${dayjs(record?.yieldanalysis?.[0]?.dateref).locale(t("short_lang")).format('ddd D/M')}`}</EditButton>}
              {<ListButton size="small" resourceNameOrRouteName="yieldanalysis"></ListButton>}
              <Button href={`${t("menu.yield")}/create/${record.id}`} size="small" icon={<PlusSquareOutlined />}>{`${t("buttons.create")} ${t("menu.yield")}`}</Button>
              <Button
                size="small"
                icon={<SyncOutlined spin={syncingId === record.id} />}
                loading={syncingId === record.id}
                disabled={syncingId !== null}
                onClick={() => resyncOne(record.id, record.name)}
              >
                Resync
              </Button>
              </Space>
          )}}
        />
      </Table>
    </List>
  );
};

interface IPriceZoneFilterVariables {
  name: string;
}
